import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Tab, Tabs } from '@mui/material';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../shared/utils/firebase';
import { computeHasFeature } from '../auth/hasFeature';
import { useAuth } from '../auth/useAuth';
import { useTaskActions } from './actions/useTaskActions';
import { InboxView } from './inbox/InboxView';
import { InboxSegment } from './inbox/inboxModel';
import { joinLocationLabels } from '../auth/managedLocations';
import { locationLabel } from './tasks/clientRecord';
import { scopeForViewer, useClientTasks } from './tasks/useClientTasks';
import { TodayView } from './today/TodayView';

type DashboardTab = 'today' | 'inbox';

const VIEW_PARAM = 'view';
const CLIENT_PARAM = 'client';
const SEGMENT_PARAM = 'segment';
const COACH_PARAM = 'coach';
/** Dashboard opens are logged at most this often per user. */
const AUDIT_THROTTLE_MS = 3 * 60 * 60 * 1000;

/** Records the dashboard visit in audit_logs, as the old dashboard did. */
function useDashboardAuditLog() {
  const { user, userMetadata, userRole } = useAuth();
  const uid = user?.uid;
  useEffect(() => {
    if (!uid) return;
    const key = `dashboard_log_last_${uid}`;
    const now = Date.now();
    try {
      const last = localStorage.getItem(key);
      if (last && now - parseInt(last, 10) < AUDIT_THROTTLE_MS) return;
    } catch {
      // Storage unavailable: log anyway.
    }
    setDoc(doc(db, 'audit_logs', `dashboard_${now}`), {
      action: 'Open Dashboard',
      actionType: 'ACCESS',
      userId: uid,
      userName: userMetadata?.displayName || userMetadata?.email || 'Unknown',
      userRole: userRole || 'Unknown',
      timestamp: serverTimestamp(),
      details: 'Dashboard Accessed',
    })
      .then(() => {
        try {
          localStorage.setItem(key, String(now));
        } catch {
          // Ignore: the next visit simply logs again.
        }
      })
      .catch((error) => console.error('Failed to log dashboard access:', error));
  }, [uid, userMetadata, userRole]);
}

/**
 * The dashboard: Today (a coach's sessions with their clients' tasks attached)
 * and the Inbox (everything outstanding, a row per client). Coaches land on
 * Today; managers and admins on the Inbox. Both read one task model.
 */
const Dashboard: React.FC<{ userRole?: string }> = () => {
  const { userRole, userMetadata, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const scope = scopeForViewer(userRole, userMetadata);
  const tasks = useClientTasks(scope);
  const actions = useTaskActions(tasks);
  useDashboardAuditLog();

  const coachId = userMetadata?.coach_id ? String(userMetadata.coach_id) : null;
  // Today reads the coach's Mindbody schedule (design §8.6), unless an admin has opted the account out
  // (feature_flags.mindbody_schedule false), which reads the client list as before.
  const mindbodySchedule = computeHasFeature(userMetadata, 'mindbody_schedule', true);
  const requested = params.get(VIEW_PARAM);
  const tab: DashboardTab = coachId && (requested === 'today' || (requested !== 'inbox' && userRole === 'USER')) ? 'today' : 'inbox';
  const selectedClient = params.get(CLIENT_PARAM);
  const segment: InboxSegment = params.get(SEGMENT_PARAM) === 'success' ? 'success' : 'programme';
  const coachFilter = params.get(COACH_PARAM);
  // Segment and coach live in the URL so Back from a client page returns to the same view; changing them replaces history.
  const setParam = (key: string, value: string | null) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (value) updated.set(key, value);
        else updated.delete(key);
        return updated;
      },
      { replace: true }
    );
  // A coach's own view needs no label: everything on it is theirs.
  const scopeLabel = scope?.kind === 'all' ? 'All locations' : scope?.kind === 'location' ? joinLocationLabels(scope.locations, locationLabel) : null;

  // Switching view or opening a client from Today adds a history entry, so Back returns to where you were;
  // moving between clients inside the Inbox does not, so Back still leaves the dashboard in one step.
  const setView = (next: DashboardTab, clientId?: string | null, push = true) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        updated.set(VIEW_PARAM, next);
        if (clientId) updated.set(CLIENT_PARAM, clientId);
        else if (clientId === null) updated.delete(CLIENT_PARAM);
        return updated;
      },
      { replace: !push }
    );

  if (!scope) {
    return (
      <Alert severity="info" sx={{ maxWidth: 720 }}>
        Your account isn't linked to a coach ID or a location yet, so there is nothing to show here. An admin can set this up in the
        Admin Panel.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1320, mx: 'auto' }}>
      {coachId ? (
        <Tabs
          value={tab}
          onChange={(_, next: DashboardTab) => setView(next, null)}
          aria-label="Dashboard views"
          sx={{ mb: 3, minHeight: 40, borderBottom: '1px solid #E3E8EF', '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600, fontSize: 15 } }}
        >
          <Tab value="today" label="Today" />
          <Tab value="inbox" label={tasks.status === 'ready' && tasks.work.length ? `Actions · ${tasks.work.length}` : 'Actions'} />
        </Tabs>
      ) : null}

      {tab === 'today' && coachId ? (
        <TodayView
          tasks={tasks}
          actions={actions}
          coachId={coachId}
          scopeLabel={scopeLabel}
          includeCollaborators={scope.kind !== 'all'}
          mindbodySchedule={mindbodySchedule}
          viewerEmail={user?.email ?? userMetadata?.email ?? null}
          onOpenInInbox={(clientId) => setView('inbox', clientId ?? null)}
        />
      ) : (
        <InboxView
          tasks={tasks}
          actions={actions}
          scopeLabel={scopeLabel}
          selectedId={selectedClient}
          onSelect={(clientId) => setView('inbox', clientId, false)}
          segment={segment}
          onSegment={(next) => setParam(SEGMENT_PARAM, next === 'success' ? 'success' : null)}
          coach={scope.kind === 'coach' ? undefined : { value: coachFilter, onChange: (next) => setParam(COACH_PARAM, next) }}
          showCoach={scope.kind !== 'coach'}
        />
      )}
      {actions.dialogs}
    </Box>
  );
};

export default Dashboard;
